import { PeopleAlt, PushPin } from '@mui/icons-material'
import { alpha, Box, Chip, Typography } from '@mui/material'
import { CandidateGroupNotes, CandidateNote } from '__generated__/graphql'
import { formatDate } from 'date-fns'
import startCase from 'lodash.startcase'
import { Link } from 'react-router-dom'
import type { CandidateNote as CandidateNoteType } from 'recruiter/graphql/candidateNotes.gql'
import { recruiterRouteConfig } from 'recruiter/routes'
import { getFullName } from 'shared/utils'
import { stripHtmlTags } from 'shared/utils/stripTags'

interface NoteListItemProps {
    note: CandidateNoteType
    isSelected: boolean
    onClick: () => void
}

const getNoteCompanyAndRoster = (note: CandidateNoteType) => {
    if (note.noteType === 'group') {
        const groupNote = note.note as CandidateGroupNotes
        return {
            company: groupNote?.groupNote?.company,
            roster: groupNote?.groupNote?.roster,
        }
    }
    const singleNote = note.note as CandidateNote
    return {
        company: singleNote?.company,
        roster: singleNote?.roster,
    }
}

const NoteListItem = ({ note, isSelected, onClick }: NoteListItemProps) => {
    const author = note.createdByUser || note.updatedByUser
    const authorName = author ? getFullName(author) : 'Unknown User'
    const plainText = stripHtmlTags(note.noteData || '')
    const { company, roster } = getNoteCompanyAndRoster(note)
    const updatedStr = formatDate(
        new Date(note.updatedAt || note.createdAt),
        'MMM d yyyy'
    )

    return (
        <Box
            onClick={onClick}
            sx={{
                px: 2,
                py: 1.5,
                cursor: 'pointer',
                borderBottom: '1px solid',
                borderColor: 'divider',
                borderLeft: isSelected ? '3px solid' : '3px solid transparent',
                borderLeftColor: isSelected ? 'primary.main' : 'transparent',
                bgcolor: isSelected ? 'action.selected' : undefined,
                '&:hover': {
                    bgcolor: isSelected ? 'action.selected' : 'grey.50',
                },
                transition: 'background-color 0.15s',
                userSelect: 'none',
            }}
        >
            {/* Row 1: Template Type + Author */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 0.5,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        minWidth: 0,
                        flex: 1,
                    }}
                >
                    <Typography variant="body2" fontWeight="bold" noWrap>
                        {startCase(note.template?.templateType)}
                    </Typography>
                    {note.noteType === 'group' && (
                        <PeopleAlt
                            sx={{
                                fontSize: 14,
                                color: 'text.secondary',
                                flexShrink: 0,
                            }}
                        />
                    )}
                    {note.isPinned && (
                        <PushPin
                            sx={{
                                fontSize: 14,
                                color: 'text.secondary',
                                flexShrink: 0,
                            }}
                        />
                    )}
                </Box>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ flexShrink: 0, ml: 1 }}
                >
                    {authorName}
                </Typography>
            </Box>

            {/* Row 2: Text preview (3 lines) */}
            {plainText && (
                <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.4,
                        mb: 0.5,
                    }}
                >
                    {plainText}
                </Typography>
            )}

            {/* Row 3: Chips */}
            {(company?.id || roster?.id) && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        flexWrap: 'wrap',
                        mb: 0.5,
                    }}
                >
                    {company?.id && (
                        <Chip
                            component={Link}
                            to={`${recruiterRouteConfig.companies.path}/${company.id}`}
                            label={company.value}
                            size="small"
                            clickable
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                textDecoration: 'none',
                                bgcolor: (theme) =>
                                    alpha(theme.palette.primary.main, 0.08),
                                color: 'primary.dark',
                                '&:hover': {
                                    bgcolor: (theme) =>
                                        alpha(theme.palette.primary.main, 0.16),
                                },
                            }}
                        />
                    )}
                    {roster?.id && (
                        <Chip
                            component={Link}
                            to={`${recruiterRouteConfig.rosters.path}/${roster.id}`}
                            label={roster.rosterName}
                            size="small"
                            clickable
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                textDecoration: 'none',
                                bgcolor: (theme) =>
                                    alpha(theme.palette.primary.main, 0.08),
                                color: 'primary.dark',
                                '&:hover': {
                                    bgcolor: (theme) =>
                                        alpha(theme.palette.primary.main, 0.16),
                                },
                            }}
                        />
                    )}
                </Box>
            )}

            {/* Row 4: Updated date */}
            <Typography variant="caption" color="text.secondary">
                {updatedStr}
            </Typography>
        </Box>
    )
}

export default NoteListItem
